// Framework7 comments thread. Same API as web CommentsPanel (commentableType + commentableId).
import React, { useEffect, useMemo, useRef, useState } from "react";
import { connect, useDispatch } from "react-redux";
import { Messages, Message, Link, f7, PhotoBrowser } from "framework7-react";
import axios from "axios";
import jsonFormData from "json-form-data";
import moment from "moment";
import _ from "lodash";

import { getComments, createComment, destroyComment, markCommentsRead } from "../actions";
import { createCommentsSubscription } from "channels/comments_channel";

import "./CommentsPanel.scss";

function mapStateToProps(state) {
  return {
    comments: state.comments,
    current_user: state.current_user,
  };
}

const toastError = (text) => {
  f7.toast.create({ text, position: "center", closeTimeout: 2000 }).open();
};

const IMAGE_EXT = ["png", "jpg", "jpeg", "gif", "bmp", "webp", "heic"];

const isImageAttachment = (attachment) => {
  const type = (attachment?.content_type || "").toLowerCase();
  const ext = (attachment?.name || "").split(".").pop().toLowerCase();
  return type.startsWith("image/") || _.includes(IMAGE_EXT, ext);
};

const attachmentViewUrl = (attachment) => attachment?.preview_url || attachment?.url;

const CommentsPanel = (props) => {
  const { commentableType, commentableId, comments, current_user } = props;
  const dispatch = useDispatch();
  const subscriptionRef = useRef(null);
  const fileRef = useRef(null);
  const photoBrowserRef = useRef(null);
  const [text, setText] = useState("");
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!commentableType || !commentableId) return;

    dispatch(getComments(commentableType, commentableId));
    subscriptionRef.current = createCommentsSubscription(dispatch, {
      commentableType,
      commentableId,
      onComment: () => dispatch(markCommentsRead(commentableType, commentableId)),
    });

    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }
    };
  }, [dispatch, commentableType, commentableId]);

  const filteredComments = useMemo(() => {
    return _.chain(comments)
      .filter((c) => c.commentable_type === commentableType && String(c.commentable_id) === String(commentableId))
      .sortBy("created_at")
      .value();
  }, [comments, commentableType, commentableId]);

  const uploadFiles = (files) => {
    if (_.isEmpty(files) || uploading || sending) return;

    setUploading(true);
    Promise.all(
      files.map((file) => {
        const data = jsonFormData({ attachment: file }, { showLeafArrayIndexes: true });
        return axios.post("/attachments/create", data).then((response) => ({
          name: file.name,
          blob_signed_id: response.data.blob_signed_id,
        }));
      })
    )
      .then((uploaded) => setPendingFiles((prev) => [...prev, ...uploaded]))
      .catch(() => toastError("Failed to upload attachment"))
      .finally(() => setUploading(false));
  };

  const handleSend = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const body = text.trim();
    if (sending || (!body && _.isEmpty(pendingFiles))) return;

    const attachments_attributes = pendingFiles.filter((f) => f.blob_signed_id).map((f) => ({ blob_signed_id: f.blob_signed_id }));

    setSending(true);
    Promise.resolve(
      dispatch(
        createComment({
          body: body || "(attachment)",
          commentable_type: commentableType,
          commentable_id: commentableId,
          attachments_attributes,
        })
      )
    )
      .then(() => {
        setText("");
        setPendingFiles([]);
        return dispatch(getComments(commentableType, commentableId));
      })
      .catch(() => toastError("Failed to post comment"))
      .finally(() => setSending(false));
  };

  const handleRemove = (comment) => {
    if (!comment || !comment.id) return;
    f7.dialog.confirm("Remove this comment?", () => {
      Promise.resolve(dispatch(destroyComment(comment.id))).then(() => dispatch(getComments(commentableType, commentableId)));
    });
  };

  const imagePhotos = useMemo(() => {
    return _.flatMap(filteredComments, (comment) =>
      _.filter(comment.attachments_attributes, isImageAttachment).map((a) => attachmentViewUrl(a)).filter(Boolean)
    );
  }, [filteredComments]);

  const openAttachment = (attachment) => {
    const url = attachmentViewUrl(attachment);
    if (!url) return;

    if (isImageAttachment(attachment) && photoBrowserRef.current) {
      const allIndex = Math.max(0, _.indexOf(imagePhotos, url));
      photoBrowserRef.current.open(allIndex);
      return;
    }

    window.open(url, "_blank");
  };

  if (!commentableType || !commentableId) return null;

  return (
    <div className="comments-mobile">
      <div className="comments-mobile__thread">
        <Messages>
          {_.isEmpty(filteredComments) && <Message type="received" text="No comments yet. Write one below." name="Comments" />}
          {_.map(filteredComments, (comment) => {
            const isOwn = current_user && current_user.id != null && comment.user_id == current_user.id;
            const attachments = _.filter(comment.attachments_attributes, (a) => a?.url || a?.preview_url);
            return (
              <div key={comment.id} className={`comments-mobile__item comments-mobile__item--${isOwn ? "sent" : "received"}`}>
                <Message
                  type={isOwn ? "sent" : "received"}
                  name={comment.user_name || "User"}
                  text={comment.body}
                  header={comment.created_at ? moment(comment.created_at).format("DD/MM HH:mm") : ""}
                />
                {_.map(attachments, (a) => {
                  const viewUrl = attachmentViewUrl(a);
                  const image = isImageAttachment(a);
                  return (
                    <div key={a.id || viewUrl} className="comments-mobile__attachment">
                      {image ? (
                        <img
                          src={viewUrl}
                          alt={a.name || "attachment"}
                          className="comments-mobile__thumb"
                          onClick={() => openAttachment(a)}
                        />
                      ) : (
                        <Link
                          href={viewUrl}
                          external
                          target="_blank"
                          className="external comments-mobile__file-link"
                          onClick={(e) => {
                            e.preventDefault();
                            openAttachment(a);
                          }}
                        >
                          {a.name || "Open attachment"}
                        </Link>
                      )}
                    </div>
                  );
                })}
                {isOwn && (
                  <Link className="comments-mobile__delete" onClick={() => handleRemove(comment)}>
                    Remove
                  </Link>
                )}
              </div>
            );
          })}
        </Messages>
      </div>

      {!_.isEmpty(pendingFiles) && (
        <div className="comments-mobile__pending">
          {pendingFiles.map((file, i) => (
            <span key={`${file.name}-${i}`}>
              {file.name}
              <Link onClick={() => setPendingFiles((prev) => prev.filter((_, idx) => idx !== i))}>x</Link>
            </span>
          ))}
        </div>
      )}

      <input
        ref={fileRef}
        type="file"
        multiple
        className="comments-mobile__file"
        onChange={(e) => {
          uploadFiles(Array.from(e.target.files || []));
          e.target.value = "";
        }}
        disabled={uploading || sending}
      />

      <div className="comments-mobile__composer">
        <Link iconF7="paperclip" onClick={() => fileRef.current && fileRef.current.click()} />
        <textarea
          className="comments-mobile__input"
          placeholder={uploading ? "Uploading..." : "Write a comment..."}
          value={text}
          rows={2}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSend(e);
            }
          }}
        />
        <Link iconF7="arrow_up_circle_fill" onClick={handleSend} />
      </div>
      <PhotoBrowser photos={imagePhotos} type="standalone" theme="dark" ref={photoBrowserRef} />
    </div>
  );
};

export default connect(mapStateToProps)(CommentsPanel);
